import { useSnackbar } from '@automata/rexports/notistack';
import {
  ErrorDisplay,
  Modal,
  TrackedButton,
  Typography,
  WarningCard,
} from '@automata/ui';
import { getErrorMessage, logger } from '@automata/utils';
import {
  useGetAllTransportConfigs,
  useGetTransportConfig,
  useListPlans,
  usePublishWorkflow,
  useSaveWorkflow,
} from '@automata/wfb-api';
import { PlannerOptionsInput } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import Stack from '@mui/system/Stack';
import { useRouter } from 'next/router';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import PlanConfirmationCheckbox from './PlanConfirmationCheckbox';
import {
  getFieldErrorsForTasks,
  setBatchesForSavingWorkflow,
} from '../../features/actions';
import { setWorkflowIsSaved } from '../../features/ui/uiSlice';
import {
  selectIsWorkflowPublished,
  selectOptimizationTarget,
  selectWorkcellTransportConfigId,
  selectWorkcellTransportConfigType,
  selectWorkcellTransportConfigVersion,
  selectWorkflowBatchNumber,
  selectWorkflowMetadata,
} from '../../features/workflow/selectors';
import { updatePlannerOptions } from '../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useValidBatches } from '../../hooks/useValidBatches';
import { PreviewBlocker } from '../../text';
import { getParameterValues, useParametersForm } from '../../utils';
import { toPreviewBlockers } from '../../utils/previewBlockers';
import BatchesInput from '../BatchesInput';
import { ModalAccordion } from '../ModalAccordion';
import PlanWorkflowAccordionGroup from '../PreviewPanel/PlanWorkflowAccordionGroup/PlanWorkflowAccordionGroup';
import ReplanWarning from '../ReplanWarning';

interface PublishWorkflowModalProps {
  onClose: () => void;
  workflowId: string;
  workflowName: string;
}

export const PublishWorkflowModal = ({
  workflowId,
  workflowName,
  onClose,
}: PublishWorkflowModalProps) => {
  const router = useRouter();
  const isWorkflowPublished = useAppSelector(selectIsWorkflowPublished);
  const workflowMetadata = useAppSelector(selectWorkflowMetadata);
  const workflowBatchNumber = useAppSelector(selectWorkflowBatchNumber);
  const workflowOptimizationTarget = useAppSelector(selectOptimizationTarget);
  const transportConfigId = useAppSelector(selectWorkcellTransportConfigId);
  const transportConfigType = useAppSelector(selectWorkcellTransportConfigType);
  const transportConfigVersion = useAppSelector(
    selectWorkcellTransportConfigVersion
  );
  const dispatch = useAppDispatch();
  const { minBatch, validBatches } = useValidBatches();
  const [batches, setBatches] = useState<number | null>(
    workflowBatchNumber || minBatch
  );
  const [blockers, setBlockers] = useState<PreviewBlocker[]>([]);
  const [publishWithPlan, setPublishWithPlan] = useState<boolean>(false);
  const [hasSetPublishWithPlan, setHasSetPublishWithPlan] =
    useState<boolean>(false);
  const [optimisationTarget, setOptimisationTarget] = useState<
    PlannerOptionsInput['optimisation_target']
  >(workflowOptimizationTarget || 'minimize_total_plan_duration');
  const {
    isParametersValid,
    parameters,
    parametersWithoutFileType,
    handleSaveParameter,
  } = useParametersForm();

  const syncedPlanId = workflowMetadata?.syncedPlanId ?? null;

  const { data: listPlansData } = useListPlans({
    pathParams: { workflowId: workflowId },
  });
  const { data: transportConfigs = [] } = useGetAllTransportConfigs({});

  const lastSuccessfulPlan = useMemo(
    () => listPlansData?.find((plan) => plan.status === 'COMPLETED') ?? null,
    [listPlansData]
  );

  const lastRequestedPlan = useMemo(() => {
    return listPlansData?.[0] ?? null;
  }, [listPlansData]);

  const resolvedTransportConfigType = useMemo(() => {
    if (transportConfigType) return transportConfigType;

    return transportConfigs.find((config) => config.id === transportConfigId)
      ?.type;
  }, [transportConfigId, transportConfigType, transportConfigs]);
  const { data: resolvedTransportConfig } = useGetTransportConfig(
    {
      queryParams: {
        id: transportConfigId ?? '',
        type: resolvedTransportConfigType ?? 'V1',
        version: transportConfigVersion,
      },
    },
    {
      enabled: Boolean(transportConfigId && resolvedTransportConfigType),
    }
  );

  const isLatestPlanSuccessful = lastRequestedPlan?.status === 'COMPLETED';
  const publishWithPlanValue = hasSetPublishWithPlan
    ? publishWithPlan
    : isLatestPlanSuccessful;

  // useEffect to update default input for batches
  // when workflow planned with new batch amount
  useEffect(() => {
    setBatches(workflowBatchNumber);
  }, [workflowBatchNumber]);

  useEffect(() => {
    setOptimisationTarget(workflowOptimizationTarget);
  }, [workflowOptimizationTarget]);

  // validate parameters
  useEffect(() => {
    const errors = dispatch(getFieldErrorsForTasks(parameters));

    if (!errors || errors.length === 0) {
      return setBlockers([]);
    }

    setBlockers(toPreviewBlockers({ isError: true, errors }));
  }, [parameters, dispatch]);

  const {
    mutateAsync: publishWorkflow,
    isLoading: isPublishing,
    error,
  } = usePublishWorkflow();

  const completedSyncedPlanId =
    lastRequestedPlan?.status === 'COMPLETED' ? syncedPlanId : null;

  const { saveWorkflow, isIdle: saveWorkflowIsIdle } = useSaveWorkflow({
    onSuccess: async () => {
      const planIdToInclude =
        publishWithPlanValue &&
        (completedSyncedPlanId || lastSuccessfulPlan?.id);
      const queryParams = planIdToInclude
        ? { plan_id_to_include: planIdToInclude, number_of_batches: batches }
        : { number_of_batches: batches };

      try {
        const pubWorkflow = await publishWorkflow({
          pathParams: { workflowId },
          queryParams,
          body: {
            parameter_values: getParameterValues(parameters),
            ...(transportConfigId && resolvedTransportConfigType
              ? {
                  transport_config_data: {
                    id: resolvedTransportConfig?.id ?? transportConfigId,
                    type:
                      resolvedTransportConfig?.type ??
                      resolvedTransportConfigType,
                    ...(transportConfigVersion !== undefined
                      ? { version: transportConfigVersion }
                      : {}),
                  },
                }
              : {}),
          },
        });
        enqueueSnackbar('Workflow published', { variant: 'success' });
        onClose();
        const orgId = router.query.org;
        router.push(
          `/${orgId}/workflows/${pubWorkflow.linq_backend_workflow_id}?tab=1`
        );
      } catch (err: unknown) {
        logger.error(getErrorMessage(err, 'unknown error'));
        enqueueSnackbar(`Failed to publish workflow`, {
          variant: 'error',
        });
        dispatch(setWorkflowIsSaved(false));
      }
    },
  });

  const { enqueueSnackbar } = useSnackbar();
  const onPlanConfirmationCheckboxChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setHasSetPublishWithPlan(true);
    setPublishWithPlan(event.target.checked);
  };

  const onSubmit = useCallback(() => {
    dispatch(
      updatePlannerOptions({
        optimisation_target: optimisationTarget,
      })
    );
    const updatedWorkflowJsonToSave = dispatch(
      setBatchesForSavingWorkflow({ batches: batches ?? 1 })
    );
    saveWorkflow(updatedWorkflowJsonToSave, workflowId);
    dispatch(setWorkflowIsSaved(true));
  }, [dispatch, optimisationTarget, batches, saveWorkflow, workflowId]);

  return (
    <Modal onClose={onClose} open title={`Publish "${workflowName}"`}>
      <Stack spacing={2}>
        <>
          <Stack spacing={2}>
            <Typography>
              Are you sure you want to publish this workflow?
            </Typography>

            {isWorkflowPublished && (
              <WarningCard message="This workflow has been published before. Publishing again will overwrite the previous published workflow." />
            )}

            {!lastSuccessfulPlan && (
              <WarningCard message="No output plan found. A plan will be generated at runtime, delaying the run’s start. Plan before publishing to prevent delays." />
            )}

            {error !== null && (
              <ErrorDisplay
                error={error}
                userMessage="Failed to publish workflow"
              />
            )}
          </Stack>
          <BatchesInput batches={batches} handleChange={setBatches} />
          <PlanWorkflowAccordionGroup
            optimizationTarget={optimisationTarget}
            parameters={parameters}
            parametersWithoutFileType={parametersWithoutFileType}
            handleSaveParameter={handleSaveParameter}
            handleOptimizationTargetChange={setOptimisationTarget}
          />
          {lastSuccessfulPlan && (
            <ModalAccordion title="Plan">
              <PlanConfirmationCheckbox
                inputBatchNumber={batches}
                lastSuccessfulPlanId={lastSuccessfulPlan?.id || null}
                publishWithPlan={publishWithPlanValue}
                savedBatchNumber={workflowBatchNumber}
                syncedPlanId={completedSyncedPlanId}
                onChange={onPlanConfirmationCheckboxChange}
              />
            </ModalAccordion>
          )}
        </>
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <TrackedButton
            trackLabel="screen.visualWorkflow.deleteCancel"
            disabled={isPublishing || !saveWorkflowIsIdle}
            color="secondary"
            variant="text"
            onClick={() => {
              onClose();
              setBatches(minBatch);
            }}
          >
            Cancel
          </TrackedButton>
          <Stack direction="row" alignItems="center" gap={1}>
            <TrackedButton
              trackLabel="screen.visualWorkflow.publish"
              loading={isPublishing || !saveWorkflowIsIdle}
              variant="contained"
              type="submit"
              disabled={
                !isParametersValid ||
                batches === null ||
                !validBatches.includes(batches ?? 0) ||
                blockers.length > 0
              }
              onClick={onSubmit}
            >
              Publish
            </TrackedButton>
            {blockers.length > 0 && (
              <ReplanWarning
                previewBlockers={blockers}
                title="Something blocks you from publishing."
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};
